import { useState } from 'react';
import type { PerfilProfesional } from '@medchoice/shared';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  Avatar, Boton, Cargando, EstadoVacio, Estrellas, FalloDeCarga, Icono, Insignia, Pestanas, Tarjeta, useTitulo,
} from '../../ui';
import { cuandoEs, pesos } from '../../ui/format';

type Pestana = 'servicios' | 'resenas' | 'obras';
const DIAS_A_MOSTRAR = 5;

export function Perfil({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => publico.perfil(id), [id]);
  useTitulo(pedido.datos && `${pedido.datos.nombre} ${pedido.datos.apellido}`);

  if (pedido.estado === 'cargando') return <Cargando texto="Cargando el perfil" />;
  if (pedido.estado === 'error' || !pedido.datos) {
    return <FalloDeCarga mensaje={pedido.error ?? 'No encontramos ese profesional'} alReintentar={pedido.recargar} />;
  }
  return <PerfilCargado profesional={pedido.datos} />;
}

function PerfilCargado({ profesional: p }: { profesional: PerfilProfesional }) {
  const [pestana, setPestana] = useState<Pestana>('servicios');
  const nombre = `${p.nombre} ${p.apellido}`;
  const reservar = (idServicio: number) => irA(`/reservar/${p.id_profesional}?servicio=${idServicio}`);

  return (
    <div className="perfil">
      <div className="perfil__principal">
        <Tarjeta>
          <header className="perfil__encabezado">
            <Avatar nombre={nombre} tamano={88} verificado />
            <div>
              <Insignia tono="confirmado" icono="shield-check">Verificado</Insignia>
              <h1>{nombre}</h1>
              <p className="perfil__meta">{p.especialidad} · {p.matricula}</p>
              <Estrellas puntaje={p.calificacion ?? 0} cantidad={p.cantidad_resenas} />
              <p className="perfil__lugar"><Icono nombre="map-pin" tamano={14} /> {p.direccion}, {p.barrio}</p>
              {p.resumen.length > 0 && (
                <div className="chips" aria-label="Lo que más destacan los pacientes">
                  {p.resumen.map((r) => <span key={r} className="chip chip--coral">{r}</span>)}
                </div>
              )}
            </div>
          </header>
          {p.bio && <p className="perfil__bio">{p.bio}</p>}
        </Tarjeta>

        <Pestanas<Pestana>
          etiqueta="Información del profesional"
          activa={pestana}
          alElegir={setPestana}
          pestanas={[
            { id: 'servicios', texto: 'Servicios', cantidad: p.servicios.length },
            { id: 'resenas', texto: 'Reseñas', cantidad: p.cantidad_resenas },
            { id: 'obras', texto: 'Obras sociales', cantidad: p.obras_sociales.length },
          ]}
        />

        {pestana === 'servicios' && (
          <div className="perfil__lista">
            {p.servicios.map((s) => (
              <Tarjeta key={s.id_servicio}>
                <div className="perfil__servicio">
                  <div>
                    <h3>{s.nombre}</h3>
                    <p>{s.duracion_min} minutos{s.permite_videoconsulta ? ' · también por videoconsulta' : ''}</p>
                  </div>
                  <strong>{pesos(s.precio)}</strong>
                  <Boton onClick={() => reservar(s.id_servicio)}>Reservar</Boton>
                </div>
              </Tarjeta>
            ))}
          </div>
        )}

        {pestana === 'resenas' && (p.resenas.length === 0
          ? <EstadoVacio icono="star" titulo="Todavía no tiene reseñas" cuerpo="Las reseñas las dejan pacientes que se atendieron." />
          : (
            <div className="perfil__lista">
              {p.resenas.map((r) => (
                <Tarjeta key={r.id_resena}>
                  <div className="perfil__resena">
                    <Estrellas puntaje={r.calificacion} />
                    <p>{r.comentario}</p>
                    <span>{r.autor} · {new Date(r.fecha.replace(' ', 'T')).toLocaleDateString('es-AR')}</span>
                  </div>
                </Tarjeta>
              ))}
            </div>
          ))}

        {pestana === 'obras' && (
          <div className="chips chips--grandes">
            {p.obras_sociales.map((o) => <span key={o} className="chip">{o}</span>)}
          </div>
        )}
      </div>

      <aside className="perfil__lateral">
        <ProximosHorarios profesional={p} />
      </aside>
    </div>
  );
}

function ProximosHorarios({ profesional: p }: { profesional: PerfilProfesional }) {
  const servicio = p.servicios[0];
  const pedido = usePedido(async () => {
    if (!servicio) return [];
    const hoy = new Date();
    const dias = Array.from({ length: DIAS_A_MOSTRAR * 2 }, (_, i) => {
      const f = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + i);
      return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
    });
    const libres = await Promise.all(dias.map(async (fecha) => ({ fecha, horas: await publico.horarios(p.id_profesional, fecha, servicio.id_servicio) })));
    return libres.filter((d) => d.horas.length > 0).slice(0, DIAS_A_MOSTRAR);
  }, [p.id_profesional]);

  return (
    <Tarjeta>
      <h2 className="perfil__lateral-titulo">Próximos horarios</h2>
      {servicio && <p className="perfil__lateral-nota">Para {servicio.nombre.toLowerCase()}</p>}
      {pedido.estado === 'cargando' && <Cargando texto="Buscando horarios" />}
      {pedido.datos?.length === 0 && <p className="perfil__lateral-nota">No tiene horarios libres en los próximos días.</p>}
      <ul className="perfil__horarios">
        {pedido.datos?.map((d) => (
          <li key={d.fecha}>
            <span>{cuandoEs(d.fecha)}</span>
            <span>{d.horas.slice(0, 3).join(' · ')}</span>
          </li>
        ))}
      </ul>
      <p className="perfil__sena">
        <Icono nombre="credit-card" tamano={15} />
        {p.cobra_sena && p.monto_sena !== null
          ? `Se cobra una seña de ${pesos(p.monto_sena)} al reservar`
          : 'No cobra seña para reservar'}
      </p>
      {servicio && <Boton anchoCompleto onClick={() => irA(`/reservar/${p.id_profesional}?servicio=${servicio.id_servicio}`)}>Ver agenda completa</Boton>}
    </Tarjeta>
  );
}
